import { useCallback, useEffect, useRef, useState } from "react";
import type { ChatMessage, ChatPresence } from "../../services/teamChatService";
import {
    connectTeamChat,
    isTeamChatConnected,
    sendTeamChatMessage,
} from "./teamChatSocket";

export function useTeamChatSocket(enabled: boolean) {
    const [connected, setConnected] = useState(false);
    const [socketError, setSocketError] = useState<string | null>(null);
    const [incoming, setIncoming] = useState<ChatMessage | null>(null);
    const [presence, setPresence] = useState<ChatPresence | null>(null);
    const enabledRef = useRef(enabled);
    enabledRef.current = enabled;

    useEffect(() => {
        if (!enabled) {
            setConnected(false);
            return undefined;
        }
        const teardown = connectTeamChat({
            onMessage: (message) => {
                if (!enabledRef.current) {
                    return;
                }
                setIncoming(message);
            },
            onPresence: (update) => {
                if (!enabledRef.current) {
                    return;
                }
                setPresence(update);
            },
            onError: (userMessage) => {
                if (!enabledRef.current) {
                    return;
                }
                setSocketError(userMessage);
            },
            onConnectionChange: (isConnected) => {
                if (!enabledRef.current) {
                    return;
                }
                setConnected(isConnected);
            },
        });
        return teardown;
    }, [enabled]);

    const send = useCallback((conversationId: number, body: string): boolean => {
        if (!isTeamChatConnected()) {
            setSocketError("Reconnecting to chat. Please try again in a moment.");
            return false;
        }
        return sendTeamChatMessage(conversationId, body);
    }, []);

    const clearSocketError = useCallback(() => setSocketError(null), []);

    return { connected, socketError, incoming, presence, send, clearSocketError };
}
